import { assign } from 'xstate';

import { CareType, updateMatchPreferences } from '@uplift/api';
import { BETTERHELP_ONBOARDING_ROUTING_CONFIG } from '@uplift/constants';
import { ProviderFilterTypeEnum, ProviderMedicalRole } from '@uplift/types';

import { BetterhelpOnboardingMachineContext } from '../types';
import { betterhelpMachineSetup } from './setup';
import { eligibilityGuard, MATCHES_LOADING_DURATION, syncRouteInvocation } from './utils';


const initialContext: BetterhelpOnboardingMachineContext = {
  isSlotUnavailable: false,
  isLoggedIn: null,
  phoneVerified: false,
  userId: '',
  matchId: null,
  seed: null,
  stateId: null,
  insuranceId: null,
  modalityId: null,
  age: null,
  specialtyIds: [],

  matchingPaymentType: null,
  isPaymentInfoSubmitted: false,

  careType: CareType.psychiatry,
  medicalRole: ProviderMedicalRole.psychiatrist,

  showTheseProviderFilters: [],
  identityFilters: {
    genderIds: [],
    faithIds: [],
    ethnicityIds: [],
  },
  experienceFilters: {
    communityIds: [],
    faithIds: [],
    ethnicityIds: [],
  },
  availabilityTimeOfDay: [],
  availabilityDayOfWeek: [],

  termsAccepted: false,

  selectedProviderId: null,
  appointment: null,
  appointmentProviderId: null,
  referrer: null,
  inviteToken: null,
  providerUrl: null,

  progressPercentage: 0,

  intakeFormSubmissionId: null,
};


const syncContextDataToDatabase = async ({ context }: { context: BetterhelpOnboardingMachineContext }) => {
  if (context.isLoggedIn && context.matchId) {
    await updateMatchPreferences(context.matchId, {
      stateId: context.stateId,
      visitReasonIds: context.specialtyIds,
      insuranceCompanyId: context.insuranceId,
      modalityId: context.modalityId,
      ageGroupId: context.age?.id,
      faithIds: context.identityFilters.faithIds,
      ethnicityIds: context.identityFilters.ethnicityIds,
      genderIds: context.identityFilters.genderIds,
      communityExpertise: {
        faithIds: context.experienceFilters.faithIds,
        ethnicityIds: context.experienceFilters.ethnicityIds,
        generalIds: context.experienceFilters.communityIds,
      },
      directFlowData: context.providerUrl ? {
        providerId: context.selectedProviderId,
        appointment: context.appointment.fromDatetime,
        publicProfileUrl: context.providerUrl,
      } : null
    });
  }
};

export const betterhelpOnboardingMachine = betterhelpMachineSetup.createMachine({
  id: 'betterhelp-matching',
  initial: 'Idle',
  invoke: { src: 'handleHistoryPop' },
  context: (): BetterhelpOnboardingMachineContext => initialContext,
  states: {
    Idle: {
      on: {
        Start: 'Initial'
      }
    },
    Initial: {
      invoke: {
        src: 'load',
        onError: {
          target: 'InvalidLink'
        },
        onDone: [
          {
            // An anonymous visitor who supplied a token but resolved without details
            // means the lookup failed without rejecting; treat it as an invalid link
            // rather than falling through to Welcome.
            target: 'InvalidLink',
            guard: ({ event }) => !event.output.isLoggedIn
              && !!event.output.inviteToken
              && !event.output.inviteDetails,
          },
          {
            target: 'SelectStep',
            actions: assign(({ event }) => event.output),
          },
        ],
      }
    },

    InvalidLink: {
      invoke: syncRouteInvocation(BETTERHELP_ONBOARDING_ROUTING_CONFIG.invalidLink),
    },

    SelectStep: {
      always: [
        {
          target: 'Welcome',
          guard: ({ context }) => !context.isLoggedIn,
        },
        {
          target: 'CreateAccount',
          guard: ({ context }) => !context.isLoggedIn,
        },
        {
          target: 'VerifyPhone',
          guard: ({ context }) => (
            (context.isLoggedIn
            && context.phoneVerified === false && !context.stateId)
          ),
        },
        {
          target: 'Where',
          guard: ({ context }) => (context.isLoggedIn && !context.stateId),
        },
        {
          target: 'Insurance',
          guard: ({ context }) => (context.isLoggedIn && !context.insuranceId)
        },
        {
          target: 'Reason',
          guard: ({ context }) => (context.isLoggedIn && !context.specialtyIds.length),
        },
        {
          target: 'MatchesLoading',
          guard: ({ context }) => context.isLoggedIn,
        },
      ],
    },

    Welcome: {
      entry: assign({ progressPercentage: 0 }),
      invoke: syncRouteInvocation(BETTERHELP_ONBOARDING_ROUTING_CONFIG.welcome),
      on: {
        'Welcome.Next': 'CreateAccount'
      }
    },
    CreateAccount: {
      entry: assign({ progressPercentage: 0 }),
      invoke: syncRouteInvocation(BETTERHELP_ONBOARDING_ROUTING_CONFIG.createAccount),
      on: {
        'CreateAccount.Next': 'LoadAfterAccountCreation',
        Back: 'Welcome'
      }
    },
    LoadAfterAccountCreation: {
      invoke: {
        src: 'load',
        onDone: {
          target: 'VerifyPhone',
          actions: assign(({ event }) => event.output)
        },
      }
    },
    VerifyPhone: {
      entry: assign({ progressPercentage: 5 }),
      invoke: syncRouteInvocation(BETTERHELP_ONBOARDING_ROUTING_CONFIG.verifyPhone),
      on: {
        'VerifyPhone.Next': [
          {
            target: 'VerifyPhoneCode',
            guard: ({ event }) => event.data.contactVerificationInitiated,
          },
          {
            target: 'Where',
          }
        ],
        Back: 'CreateAccount'
      }
    },
    VerifyPhoneCode: {
      entry: assign({ progressPercentage: 5 }),
      invoke: syncRouteInvocation(BETTERHELP_ONBOARDING_ROUTING_CONFIG.verifyPhoneCode),
      on: {
        'VerifyPhoneCode.Next': {
          target: 'Where',
          actions: [
            assign({
              phoneVerified: ({ event }) => event.data.phoneVerified
            }),
          ]
        },
        Back: 'VerifyPhone'
      }
    },
    Where: {
      entry: assign({ progressPercentage: 10 }),
      invoke: syncRouteInvocation(BETTERHELP_ONBOARDING_ROUTING_CONFIG.where),
      on: {
        'Where.Next': {
          target: 'Insurance',
          actions: [
            assign({
              stateId: ({ event }) => event.data.stateId,
            }),
            syncContextDataToDatabase,
          ]
        },
        'Where.NotAvailable': {
          target: 'NotAvailable',
          actions: [
            assign({
              stateId: ({ event }) => event.data.stateId,
            }),
            syncContextDataToDatabase
          ]
        },
        Back: 'VerifyPhone'
      },
    },
    NotAvailable: {
      invoke: syncRouteInvocation(BETTERHELP_ONBOARDING_ROUTING_CONFIG.notAvailable),
      on: {
        Back: 'Where'
      }
    },
    Insurance: {
      entry: assign({ progressPercentage: 20 }),
      invoke: syncRouteInvocation(BETTERHELP_ONBOARDING_ROUTING_CONFIG.insurance),
      always: {
        target: 'Where',
        guard: ({ context }) => !context.stateId,
      },
      on: {
        'Insurance.Next': [
          {
            actions: [
              assign({
                insuranceId: ({ event }) => event.data.insuranceId,
              }),
              syncContextDataToDatabase
            ],
            guard: ({ context }) => !!context.inviteDetails?.sessionCount,
            target: 'BookingInterstitial',
          },
          {
            actions: [
              assign({
                insuranceId: ({ event }) => event.data.insuranceId,
              }),
              syncContextDataToDatabase
            ],
            target: 'Reason',
          },
        ],
        Back: 'Where'
      }
    },
    BookingInterstitial: {
      entry: assign({
        progressPercentage: 25,
        medicalRole: ProviderMedicalRole.psychiatrist,
      }),
      invoke: syncRouteInvocation(BETTERHELP_ONBOARDING_ROUTING_CONFIG.bookingInterstitial, { replace: true }),
      on: {
        'BookingInterstitial.Next': [
          { target: 'Reason' }
        ],
        Back: 'Insurance'
      },
    },
    Reason: {
      entry: assign({ progressPercentage: 40 }),
      invoke: syncRouteInvocation(BETTERHELP_ONBOARDING_ROUTING_CONFIG.reason),
      on: {
        'Reason.Next': [
          {
            target: 'ProviderFilters',
            actions: [
              assign({ specialtyIds: ({ event }) => event.data.specialtyIds }),
              syncContextDataToDatabase,
            ],
          }
        ],
        Back: 'Insurance'
      }
    },
    ProviderFilters: {
      entry: assign({ progressPercentage: 60.5 }),
      invoke: syncRouteInvocation(BETTERHELP_ONBOARDING_ROUTING_CONFIG.providerFilters),
      always: [
        {
          target: 'MatchesLoading',
        },
      ],
      on: {
        'ProviderFilters.Next': {
          target: 'ProviderIdentityFilter',
          actions: assign({ showTheseProviderFilters: ({ event }) => event.data.providerFilters })
        },
        Back: 'Reason'
      }
    },
    ProviderIdentityFilter: {
      entry: assign({ progressPercentage: 62 }),
      invoke: syncRouteInvocation(BETTERHELP_ONBOARDING_ROUTING_CONFIG.providerIdentityFilter),
      always: [
        {
          target: 'ProviderExperienceFilter',
          guard: ({ context }) => !context.showTheseProviderFilters.includes(ProviderFilterTypeEnum.Identity),
        },
      ],
      on: {
        'ProviderIdentityFilter.Next': {
          target: 'ProviderExperienceFilter',
          actions: [
            assign({ identityFilters: ({ event }) => event.data }),
            syncContextDataToDatabase,
          ]
        },
        Back: 'ProviderFilters'
      }
    },
    ProviderExperienceFilter: {
      entry: assign({ progressPercentage: 63 }),
      invoke: syncRouteInvocation(BETTERHELP_ONBOARDING_ROUTING_CONFIG.providerExperienceFilter),
      always: [
        {
          target: 'MatchesLoading',
          guard: ({ context }) => !context.showTheseProviderFilters.includes(ProviderFilterTypeEnum.Experience),
        },
      ],
      on: {
        'ProviderExperienceFilter.Next': {
          target: 'MatchesLoading',
          actions: [
            assign({ experienceFilters: ({ event }) => event.data }),
            syncContextDataToDatabase,
          ]
        },
        Back: [
          {
            target: 'ProviderIdentityFilter',
            guard: ({ context }) => context.showTheseProviderFilters.includes(ProviderFilterTypeEnum.Identity),
          },
          'ProviderFilters'
        ]
      }
    },
    MatchesLoading: {
      invoke: syncRouteInvocation(BETTERHELP_ONBOARDING_ROUTING_CONFIG.matchesLoading),
      always: {
        target: 'Where',
        guard: ({ context }) => !context.stateId,
      },
      after: {
        [MATCHES_LOADING_DURATION]: [
          { target: 'Matches' },
        ]
      },
      on: {
        Back: [
          {
            target: 'Reason',
          },
          'ProviderFilters'
        ]
      }
    },
    Matches: {
      entry: assign({
        progressPercentage: 65,
        medicalRole: ProviderMedicalRole.psychiatrist,
      }),
      invoke: syncRouteInvocation(BETTERHELP_ONBOARDING_ROUTING_CONFIG.matches),
      always: {
        target: 'Where',
        guard: ({ context }) => !context.stateId,
      },
      on: {
        Back: [
          {
            target: 'Reason',
            guard: ({ context }) => context.careType === CareType.psychiatry,
          },
          {
            target: 'ProviderExperienceFilter',
            guard: ({ context }) => context.showTheseProviderFilters.includes(ProviderFilterTypeEnum.Experience),
          },
          {
            target: 'ProviderIdentityFilter',
            guard: ({ context }) => context.showTheseProviderFilters.includes(ProviderFilterTypeEnum.Identity),
          },
          'ProviderFilters'
        ],
        'Matches.UpdateFilters': {
          actions: [
            assign({
              insuranceId: ({ event }) => event.data.insuranceId,
              specialtyIds: ({ event }) => event.data.specialtyIds,
              identityFilters: ({ event }) => event.data.identityFilters,
              experienceFilters: ({ event }) => event.data.experienceFilters,
              availabilityTimeOfDay: ({ event }) => event.data.availabilityTimeOfDay,
              availabilityDayOfWeek: ({ event }) => event.data.availabilityDayOfWeek,
            }),
            syncContextDataToDatabase,
          ]
        },
        'Matches.SelectProvider': {
          target: 'Match',
          actions: assign({
            selectedProviderId: ({ event }) => event.data.providerId,
            matchingPaymentType: ({ event }) => event.data.matchingPaymentType,
          }),
        }
      }
    },
    Match: {
      invoke: syncRouteInvocation(BETTERHELP_ONBOARDING_ROUTING_CONFIG.match),
      on: {
        'Match.ScheduleSession': {
          target: 'AboutYou',
          actions: assign({ appointment: ({ event }) => event.data.appointment })
        },
        Back: {
          target: 'Matches',
          actions: assign({ selectedProviderId: null })
        }
      }
    },
    AboutYou: {
      entry: assign({ progressPercentage: 70 }),
      invoke: syncRouteInvocation(BETTERHELP_ONBOARDING_ROUTING_CONFIG.aboutYou),
      on: {
        'AboutYou.Next': 'InsuranceInfo',
        Back: 'Match'
      }
    },
    InsuranceInfo: {
      entry: assign({ progressPercentage: 75 }),
      invoke: syncRouteInvocation(BETTERHELP_ONBOARDING_ROUTING_CONFIG.insuranceInfo),
      // If the user is self-pay, they skip the insurance info screen
      always: [
        {
          guard: eligibilityGuard(({ hasInsurance }) => !hasInsurance),
          target: 'BookingPaymentInfo'
        },
      ],
      on: {
        'InsuranceInfo.Next': [
          {
            target: 'BookingPaymentInfo',
            actions: [
              assign({ insuranceId: ({ event }) => event.data.insuranceId }),
              syncContextDataToDatabase,
            ]
          },
        ],
        Back: 'AboutYou'
      }
    },
    BookingPaymentInfo: {
      entry: assign({ progressPercentage: 80 }),
      invoke: syncRouteInvocation(BETTERHELP_ONBOARDING_ROUTING_CONFIG.paymentInfo),
      always: [
        {
          target: 'ConfirmBooking',
          guard: 'isInsuranceMedicaid',
        },
        {
          target: 'ConfirmBooking',
          guard: eligibilityGuard(({ isBookingSponsored }) => isBookingSponsored),
        }
      ],
      on: {
        Back: [
          {
            guard: eligibilityGuard(({ hasInsurance }) => !hasInsurance),
            target: 'AboutYou',
          },
          'InsuranceInfo'
        ],
        'PaymentInfo.Next': [
          {
            target: 'ConfirmBooking',
            actions: [
              assign({ isPaymentInfoSubmitted: true }),
              syncContextDataToDatabase,
            ]
          },
        ],
      }
    },
    ConfirmBooking: {
      entry: assign({ progressPercentage: 85 }),
      invoke: syncRouteInvocation(BETTERHELP_ONBOARDING_ROUTING_CONFIG.confirmBooking),
      on: {
        Back: [
          {
            target: 'InsuranceInfo',
            guard: 'isInsuranceMedicaid',
          },
          {
            target: 'InsuranceInfo',
            guard: eligibilityGuard(({ isBookingSponsored, hasInsurance }) => isBookingSponsored && hasInsurance),
          },
          {
            target: 'AboutYou',
            guard: eligibilityGuard(({ isBookingSponsored, hasInsurance }) => isBookingSponsored || !hasInsurance),
          },
          {
            target: 'BookingPaymentInfo'
          },
        ],
        'ConfirmBooking.Next': [
          {
            target: 'BookingSuccess',
            actions: [
              assign({
                termsAccepted: true,
                intakeFormSubmissionId: ({ event }) => event.data.intakeFormSubmissionId
              }),
              syncContextDataToDatabase,
            ]
          }
        ],
        'ConfirmBooking.BookingConflict': {
          target: 'Match',
          actions: assign({ isSlotUnavailable: true }),
        },
      }
    },

    BookingSuccess: {
      entry: assign({ progressPercentage: 100 }),
      invoke: syncRouteInvocation(BETTERHELP_ONBOARDING_ROUTING_CONFIG.bookingSuccess),
    }
  }
});
